/**
 * Utility functions for chart axis granularity configuration
 */

export type GranularityPreset = "auto" | "years" | "quarters" | "months" | "days";

/**
 * Format date based on granularity preset
 */
export function formatDateByGranularity(date: Date, granularity: GranularityPreset): string {
  if (!date || isNaN(date.getTime())) {
    return "";
  }
  
  switch (granularity) {
    case "years":
      return date.getFullYear().toString();
    case "quarters": {
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      return `Q${quarter} ${date.getFullYear()}`;
    }
    case "months":
      return date.toLocaleString("default", {
        month: "short",
        year: "numeric",
      });
    case "days": {
      // Include year if it's different from current year or spans multiple years
      const currentYear = new Date().getFullYear();
      const dateYear = date.getFullYear();
      if (dateYear !== currentYear) {
        return date.toLocaleDateString("default", {
          month: "short",
          day: "numeric",
          year: "2-digit",
        });
      }
      return date.toLocaleDateString("default", {
        month: "short",
        day: "numeric",
      });
    }
    case "auto":
    default:
      // Auto formatting based on data range will be handled by ECharts
      return date.toLocaleString("default", {
        month: "short",
        year: "numeric",
      });
  }
}

/**
 * Calculate optimal tick interval based on data range and granularity
 */
export function calculateTickInterval(
  dates: string[],
  granularity: GranularityPreset,
  minTicks?: number,
  maxTicks?: number
): number | "auto" {
  if (granularity === "auto") {
    return "auto";
  }

  if (!dates.length) {
    return "auto";
  }

  const totalDataPoints = dates.length;
  const defaultMinTicks = minTicks || 3;
  const defaultMaxTicks = maxTicks || 8; // Reduced to prevent overcrowding

  // For time-based granularity, calculate based on actual date spans
  const firstDate = new Date(dates[0]);
  const lastDate = new Date(dates[dates.length - 1]);
  const timeSpanMonths = (lastDate.getFullYear() - firstDate.getFullYear()) * 12 + 
                        (lastDate.getMonth() - firstDate.getMonth());

  switch (granularity) {
    case "years": {
      // Calculate interval to show approximately one tick per year
      const yearSpan = lastDate.getFullYear() - firstDate.getFullYear() + 1;
      const yearInterval = Math.max(1, Math.ceil(yearSpan / defaultMaxTicks));
      // Convert to data point intervals (assuming roughly monthly data)
      return Math.max(1, Math.floor(totalDataPoints / Math.min(yearSpan, defaultMaxTicks)));
    }
    case "quarters": {
      // Show quarterly ticks
      const quarterSpan = Math.ceil(timeSpanMonths / 3);
      return Math.max(1, Math.floor(totalDataPoints / Math.min(quarterSpan, defaultMaxTicks)));
    }
    case "months": {
      // Show monthly ticks, but not too many
      const monthInterval = Math.max(1, Math.ceil(timeSpanMonths / defaultMaxTicks));
      return Math.max(1, Math.floor(totalDataPoints * monthInterval / timeSpanMonths));
    }
    case "days":
      // For daily granularity, show fewer ticks to avoid overcrowding
      return Math.max(1, Math.floor(totalDataPoints / Math.min(defaultMaxTicks, 6)));
    default:
      return "auto";
  }
}

/**
 * Get optimal number of ticks based on chart dimensions and granularity
 */
export function getOptimalTickCount(
  chartWidth: number,
  granularity: GranularityPreset,
  minTicks?: number,
  maxTicks?: number
): number {
  const defaultMinTicks = minTicks || 3;
  const defaultMaxTicks = maxTicks || 10;

  // Base calculation on available width
  const minPixelsPerTick = granularity === "days" ? 40 : 60; // Days need less space
  const maxPossibleTicks = Math.floor(chartWidth / minPixelsPerTick);

  return Math.min(defaultMaxTicks, Math.max(defaultMinTicks, maxPossibleTicks));
}

/**
 * Create axis label formatter function based on granularity
 */
export function createAxisLabelFormatter(granularity: GranularityPreset) {
  return (value: string | number, index?: number) => {
    if (typeof value === "string") {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return formatDateByGranularity(date, granularity);
      }
    }
    return String(value);
  };
}

/**
 * Determine if dates should show year based on granularity and data range
 */
export function shouldShowYear(dates: string[], granularity: GranularityPreset): boolean {
  if (granularity === "years") return true;
  if (granularity === "days") return false;

  // For auto, months, quarters - show year if data spans multiple years
  if (!dates.length) return true;

  const firstDate = new Date(dates[0]);
  const lastDate = new Date(dates[dates.length - 1]);
  
  return firstDate.getFullYear() !== lastDate.getFullYear();
}

/**
 * Calculate smart tick positions for better label distribution
 */
export function calculateSmartTickPositions(
  dates: string[],
  granularity: GranularityPreset,
  maxTicks: number
): number[] {
  if (!dates.length || granularity === "auto") {
    return [];
  }

  const positions: number[] = [];
  const totalPoints = dates.length;

  switch (granularity) {
    case "years": {
      // Find positions where year changes
      const years = new Set<number>();
      dates.forEach((dateStr, index) => {
        const year = new Date(dateStr).getFullYear();
        if (!years.has(year) && positions.length < maxTicks) {
          years.add(year);
          positions.push(index);
        }
      });
      break;
    }

    case "quarters": {
      // Find positions where quarter changes
      const quarters = new Set<string>();
      dates.forEach((dateStr, index) => {
        const date = new Date(dateStr);
        const quarter = `${date.getFullYear()}-Q${Math.floor(date.getMonth() / 3) + 1}`;
        if (!quarters.has(quarter) && positions.length < maxTicks) {
          quarters.add(quarter);
          positions.push(index);
        }
      });
      break;
    }

    case "months": {
      // Find positions where month changes
      const months = new Set<string>();
      dates.forEach((dateStr, index) => {
        const date = new Date(dateStr);
        const month = `${date.getFullYear()}-${date.getMonth()}`;
        if (!months.has(month) && positions.length < maxTicks) {
          months.add(month);
          positions.push(index);
        }
      });
      break;
    }

    case "days": {
      // Evenly distribute day positions
      const interval = Math.max(1, Math.floor(totalPoints / maxTicks));
      for (let i = 0; i < totalPoints && positions.length < maxTicks; i += interval) {
        positions.push(i);
      }
      break;
    }
  }

  // Always include first and last positions if not already included
  if (positions.length > 0) {
    if (positions[0] !== 0) {
      positions.unshift(0);
    }
    if (positions[positions.length - 1] !== totalPoints - 1) {
      positions.push(totalPoints - 1);
    }
  }

  return positions.slice(0, maxTicks);
}

/**
 * Determine if a label should be shown based on granularity and position
 */
export function shouldShowLabel(
  index: number,
  value: string,
  allDates: string[],
  granularity: GranularityPreset
): boolean {
  if (granularity === "auto") {
    return true; // Let ECharts handle auto mode
  }

  const currentDate = new Date(value);
  if (isNaN(currentDate.getTime())) {
    return false;
  }

  // Always show first label
  if (index === 0) {
    return true;
  }

  // Always show last label, but only if it represents a different period
  if (index === allDates.length - 1) {
    return !hasSamePeriod(currentDate, new Date(allDates[0]), granularity);
  }

  // For middle positions, only show if this is the FIRST occurrence of this period
  return isFirstOccurrenceOfPeriod(index, currentDate, allDates, granularity);
}

/**
 * Check if this is the first occurrence of a time period in the dataset
 */
function isFirstOccurrenceOfPeriod(
  currentIndex: number,
  currentDate: Date,
  allDates: string[],
  granularity: GranularityPreset
): boolean {
  // Check all previous dates to see if we've already seen this period
  for (let i = 0; i < currentIndex; i++) {
    const prevDate = new Date(allDates[i]);
    if (hasSamePeriod(currentDate, prevDate, granularity)) {
      return false; // We've seen this period before
    }
  }
  return true; // This is the first occurrence
}

/**
 * Check if two dates belong to the same period based on granularity
 */
function hasSamePeriod(date1: Date, date2: Date, granularity: GranularityPreset): boolean {
  switch (granularity) {
    case "years":
      return date1.getFullYear() === date2.getFullYear();
      
    case "quarters": {
      const quarter1 = Math.floor(date1.getMonth() / 3);
      const quarter2 = Math.floor(date2.getMonth() / 3);
      return date1.getFullYear() === date2.getFullYear() && quarter1 === quarter2;
    }
      
    case "months":
      return date1.getFullYear() === date2.getFullYear() && 
             date1.getMonth() === date2.getMonth();
             
    case "days":
      return date1.getFullYear() === date2.getFullYear() && 
             date1.getMonth() === date2.getMonth() &&
             date1.getDate() === date2.getDate();
             
    default:
      return false;
  }
}

/**
 * Get ECharts axis configuration for specific granularity
 * This pre-calculates which indices should show labels
 */
export function getGranularityAxisConfig(dates: string[], granularity: GranularityPreset) {
  const indicesToShow = new Set<number>();
  const seenPeriods = new Set<string>();
  
  // Find all unique periods and their first occurrence
  const periodOccurrences: { index: number; periodKey: string; date: Date }[] = [];
  
  for (let i = 0; i < dates.length; i++) {
    const date = new Date(dates[i]);
    const periodKey = getPeriodKey(date, granularity);
    
    if (!seenPeriods.has(periodKey)) {
      periodOccurrences.push({ index: i, periodKey, date });
      seenPeriods.add(periodKey);
    }
  }
  
  // Smart label selection: avoid showing labels that are too close together
  if (periodOccurrences.length <= 1) {
    // If only one period, show it
    periodOccurrences.forEach(p => indicesToShow.add(p.index));
  } else {
    // For multiple periods, use smart spacing
    const totalDataPoints = dates.length;
    const minSpacing = Math.max(1, Math.floor(totalDataPoints / 10)); // Minimum spacing between labels
    
    // Always include first period if it has good spacing
    const first = periodOccurrences[0];
    const second = periodOccurrences[1];
    
    if (periodOccurrences.length === 2) {
      // Only two periods - show both if reasonably spaced
      if (second.index - first.index >= minSpacing) {
        indicesToShow.add(first.index);
      }
      indicesToShow.add(second.index);
    } else {
      // Multiple periods - skip first label to avoid clutter at the beginning
      // Start from the second period for cleaner appearance
      
      // Add middle periods (starting from second)
      for (let i = 1; i < periodOccurrences.length - 1; i++) {
        indicesToShow.add(periodOccurrences[i].index);
      }
      
      // Always add last period
      const last = periodOccurrences[periodOccurrences.length - 1];
      indicesToShow.add(last.index);
      
      // Only add first period if we have very few labels total
      if (periodOccurrences.length <= 3) {
        const first = periodOccurrences[0];
        const second = periodOccurrences[1];
        // Only show first if it's very well spaced from second
        if (second.index - first.index >= minSpacing * 2) {
          indicesToShow.add(first.index);
        }
      }
    }
  }
  
  return {
    interval: (index: number) => {
      return indicesToShow.has(index);
    }
  };
}

/**
 * Get a unique period key for grouping dates
 */
function getPeriodKey(date: Date, granularity: GranularityPreset): string {
  switch (granularity) {
    case "years":
      return `${date.getFullYear()}`;
    case "quarters": {
      const quarter = Math.floor(date.getMonth() / 3);
      return `${date.getFullYear()}-Q${quarter}`;
    }
    case "months":
      return `${date.getFullYear()}-${date.getMonth()}`;
    case "days":
      return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    default:
      return date.toISOString();
  }
}

/**
 * Check if the provided data appears to be time-based
 */
export function isTimeBasedData(dates: string[]): boolean {
  if (!dates || dates.length === 0) return false;
  
  // Try to parse first few dates to see if they're valid dates
  const sampleSize = Math.min(3, dates.length);
  let validDates = 0;
  
  for (let i = 0; i < sampleSize; i++) {
    const date = new Date(dates[i]);
    if (!isNaN(date.getTime())) {
      validDates++;
    }
  }
  
  // If most samples are valid dates, assume it's time-based
  return validDates >= sampleSize * 0.7;
}
